<script lang="ts" setup>
	import { useBreakpoints, useScroll, breakpointsTailwind } from '@vueuse/core'
	import { Splide, SplideSlide } from '@splidejs/vue-splide'
	import '@splidejs/vue-splide/css'

	const { y } = useScroll(import.meta.client ? document : null)
	const founderEl = ref<null | HTMLElement[]>(null)
	const foundersWrapper = ref<null | HTMLElement>(null)
	const foundersMobileSlider = useTemplateRef('foundersMobileSlider')
	const breakpoints = useBreakpoints(breakpointsTailwind)
	const isMobile = breakpoints.smaller('md')
	const founders = [
		{
			name: 'Adebowale Adebanjo',
			title: 'Empathy',
			description: `	At MyCoverGenius, we understand the challenges that come with limited access to insurance in
									Nigeria. <br /><br />
									Many businesses and individuals are left vulnerable when the unexpected occurs—whether it's a
									staff's health emergency, damaged or stolen business gadget, or a disrupted business trip.
									<br /><br />
									These challenges create financial strain on businesses and the overall wellbeing of the
									workforce.`,
			img: '/v1727082896/Adebowale_mx71rn.webp'
		},
		{
			name: 'Alexander Igwe',
			title: 'Diverse Options',
			description: `That’s why we created MyCoverGenius - a one-stop shop for all your insurance needs. <br/><br/>Through innovative technology and co-creation of unique products in partnership with leading insurance companies, we are making insurance accessible and affordable to Nigerians.`,
			img: '/v1727082895/Alex_gjrfll.webp'
		},
		{
			name: 'Fred Ebho',
			title: 'Effortless Insurance',
			description:
				'Our comprehensive coverage—spanning Health, Auto, Gadget, and Travel insurance—has you covered, so you can focus on what truly matters, growing your business and impact.',
			img: '/v1727082895/Fred_udludv.webp'
		}
	]
	const higlightedCount = ref(0)
	const highlightedFounder = computed(() => founders[higlightedCount.value])
	const { observeElementInViewport } = useInterSector()

	const switchCount = async (count?: number) => {
		if (count !== undefined) {
			higlightedCount.value = count
			if (isMobile.value) {
				foundersMobileSlider.value?.go(count)
			}
		}
	}
	watch(y, () => {
		if (foundersWrapper.value && founderEl.value && !isMobile.value) {
			observeElementInViewport(foundersWrapper.value, isInView => {
				if (isInView) {
					if (Array.isArray(founderEl.value)) {
						founders.forEach((founder, index) => {
							const currentEl = document.querySelector(`[data-founder="${founder.name}"]`)
							if (currentEl) {
								observeElementInViewport(
									currentEl!,
									isFounderInView => {
										if (isFounderInView && !isMobile.value) {
											switchCount(index)
										}
									},
									0.8
								)
							}
						})
					}
				}
			})
		}
	})
</script>

<template>
	<div class="flexed justify-center flex-col">
		<ui-title-header
			title="Our Story"
			:ui="{ size: 'md:text-[3.25em] text-[2em] leading-[38.4px] md:leading-[62.4px]' }"
		/>

		<div class="flex flex-none flex-wrap md:justify-center items-stretch w-full mt-10">
			<div class="flex md:hidden gap-x-4 items-center w-full justify-center py-5">
				<button
					v-for="(count, index) in founders.length"
					:key="'control-btn-' + index"
					types="button"
					aria-label="`Go to ${count + 1}th founder`"
					class="w-8 h-8 flex-none custom-transition-2 flexed border justify-center rounded-full text-center text-sm"
					:class="[
						index === higlightedCount
							? 'text-white bg-blue-950 font-semibold border-blue-950'
							: 'border-gray-500 text-blue-950 font-medium'
					]"
					@click="switchCount(index)"
				>
					{{ index + 1 }}
				</button>
			</div>

			<div
				class="item md:bg-[#084163] w-full md:flex-1 flex-none flexed flex-col items-center md:px-5 pt-3 md:pt-10 md:py-10 lg:p-0"
				ref="foundersWrapper"
			>
				<div class="gap-x-3 md:inline-flex ml-auto px-5 sticky top-[5rem] translate-y-[7rem] hidden">
					<button
						v-for="(count, index) in founders.length"
						:key="'founders-' + index"
						type="button"
						class="w-3.5 h-3.5 rounded-full border"
						:class="[higlightedCount === index ? 'bg-base-primary border-[#ECF8F5]' : 'bg-gray-300']"
						@click="switchCount(index)"
					/>
				</div>
				<!-- Dektop -->
				<ui-founders-template
					v-for="(founder, index) in founders"
					:key="'founder description' + index"
					ref="founderEl"
					:title="founder.title"
					:description="founder.description"
					:data-founder="founder.name"
					itemscope
					itemtype="https://schema.org/Person"
					class="md:inline-flex hidden lg:h-[80vh] h-full justify-start"
					data-item="founder"
				>
					<meta itemprop="name" :content="founder.name" />
					<meta itemprop="jobTitle" content="Founder" />
				</ui-founders-template>

				<!-- Mobile -->
				<splide
					ref="foundersMobileSlider"
					class="w-full px-4 flex md:hidden"
					:options="{
						type: 'slide',
						perPage: 1.1,
						gap: 10,
						drag: false,
						pagination: false,
						arrows: false,
						snap: true,
						focus: 'center'
					}"
				>
					<splide-slide
						v-for="founder in founders"
						:key="'founder description' + founder.name"
						class="w-full h-full"
					>
						<ui-founders-template
							:title="founder.title"
							:description="founder.description"
							class="flex-none bg-[#084163] max-md:rounded-md w-full h-full pt-8 flex flex-col justify-around !max-w-full"
						>
							<nuxt-img
								provider="cloudinary"
								:src="founder.img"
								:alt="founder.name"
								loading="lazy"
								class="max-h-[364px] object-cover object-left md:max-h-[760px] xs:max-h-[404px] mt-auto mx-auto"
							/>
						</ui-founders-template>
					</splide-slide>
				</splide>
			</div>

			<div class="item w-full flex-none md:flex-1 md:inline-flex hidden">
				<nuxt-img
					provider="cloudinary"
					:src="highlightedFounder.img"
					:alt="highlightedFounder.name"
					loading="lazy"
					class="h-full object-cover object-left max-h-[760px] xl:max-h-[95vh] w-full sticky top-[4rem] left-0"
				/>
			</div>
		</div>
	</div>
</template>

<style></style>
